// DepartureRow.jsx
import React, { useState, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";
import SoftTypography from "components/SoftComponents/SoftTypography";
import moment from "moment";
import { feedConfig, getFeedTimeValue, getOccupancyTally } from "./LiveMonitorUtils";

const DepartureRow = memo(({ dep, timeMode, baseFeed, onArrival, onAnomaly, reportStatus, onViewAnalysis }) => {
  const [now, setNow] = useState(moment().unix());

  useEffect(() => {
    const interval = setInterval(() => setNow(moment().unix()), 1000);
    return () => clearInterval(interval);
  }, []);

  // ... (keep hooks and constants)

  const isArrived = reportStatus === 'arrived';
  const isAnomaly = reportStatus === 'anomaly';
  const isActioned = !!reportStatus;

  // Extract the static scheduled epoch from the matchKey
  const scheduledEpoch = Number(dep.matchKey.split("_")[1]);

  // ELIMINATE HYPHENS: Fallback to scheduled time if realtime feeds are momentarily null
  const safeDep = {
    ...dep,
    feed1Epoch: dep.feed1Epoch || scheduledEpoch,
    feed2Epoch: dep.feed2Epoch || scheduledEpoch,
    // Turn off the flashing "hourglass" realtime icon if we are using the static fallback
    feed1Realtime: dep.feed1Epoch ? dep.feed1Realtime : false,
    feed2Realtime: dep.feed2Epoch ? dep.feed2Realtime : false,
  };

  const f1Str = getFeedTimeValue(safeDep, "feed1", timeMode, baseFeed, now);
  const f2Str = getFeedTimeValue(safeDep, "feed2", timeMode, baseFeed, now);

  const f1Icon = safeDep.feed1Realtime && safeDep.feed1Active && f1Str !== "⊘" ? "⏳" : "";
  const f2Icon = safeDep.feed2Realtime && safeDep.feed2Active && f2Str !== "⊘" ? "⏳" : "";

  const routeName = dep.routeShortName || dep.route_short_name || "Unknown Route";
  const headsign = dep.tripHeadsign || dep.trip_headsign || "Unknown Destination";

  const arrivalText = (isArrived && dep.arrivalEpoch)
    ? `(Arrived ${moment.unix(dep.arrivalEpoch).format("HH:mm:ss")})`
    : "";

  return (
    <Box sx={{ py: 2, borderBottom: "1px solid #f0f2f5", display: "flex", flexDirection: { xxs: "column", md: "row" }, alignItems: { xxs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2 }}>

      {/* LEFT AREA: Route Title and Feeds / Stats */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <SoftTypography variant="body2" fontWeight="bold" color="dark" sx={{ lineHeight: 1.3 }}>
            {routeName} - {headsign}
          </SoftTypography>

          {arrivalText && (
            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#10b981", bgcolor: "#ecfdf5", px: 1, py: 0.5, borderRadius: 1 }}>
              {arrivalText}
            </SoftTypography>
          )}
        </Box>

        {/* CONDITIONAL RENDER: Show Stats if Arrived, else show active tracking */}
        {isArrived && dep.stats ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 0.5, columnGap: 2, mt: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: feedConfig.feed1.colour, flexShrink: 0 }} />
              <SoftTypography variant="caption" fontWeight="medium" sx={{ color: feedConfig.feed1.colour, fontFamily: "monospace", fontSize: "11px" }}>
                Final Err: {dep.stats.feed1?.finalError ?? '-'}s | MAE: {dep.stats.feed1?.mae ?? '-'}s
              </SoftTypography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: feedConfig.feed2.colour, flexShrink: 0 }} />
              <SoftTypography variant="caption" fontWeight="medium" sx={{ color: feedConfig.feed2.colour, fontFamily: "monospace", fontSize: "11px" }}>
                Final Err: {dep.stats.feed2?.finalError ?? '-'}s | MAE: {dep.stats.feed2?.mae ?? '-'}s
              </SoftTypography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 0.5, columnGap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: feedConfig.feed1.colour, flexShrink: 0 }} />
              <SoftTypography variant="button" fontWeight="medium" sx={{ color: feedConfig.feed1.colour, fontFamily: "monospace", fontSize: "12px" }}>
                {f1Str}{f1Icon} {f1Str !== "⊘" && `(${getOccupancyTally(dep.feed1Occupancy)})`}
              </SoftTypography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: feedConfig.feed2.colour, flexShrink: 0 }} />
              <SoftTypography variant="button" fontWeight="medium" sx={{ color: feedConfig.feed2.colour, fontFamily: "monospace", fontSize: "12px" }}>
                {f2Str}{f2Icon} {f2Str !== "⊘" && `(${getOccupancyTally(dep.feed2Occupancy)})`}
              </SoftTypography>
            </Box>
          </Box>
        )}
      </Box>

      {/* RIGHT AREA: Action Buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", width: { xxs: "100%", md: "110px" }, gap: 1, flexShrink: 0 }}>
        <Button
          variant={isArrived ? "contained" : "outlined"}
          size="small"
          color={isArrived ? "success" : "dark"}
          disabled={isActioned}
          onClick={(e) => {
            e.stopPropagation();
            onArrival(dep);
          }}
          sx={{ width: "100%", fontSize: "11px", padding: "4px 8px", minHeight: "30px", textTransform: "none" }}
        >
          {isArrived ? "Arrived ✅" : "Arrived"}
        </Button>

        <Button
          variant={isAnomaly ? "contained" : "outlined"}
          size="small"
          color="error"
          disabled={isActioned}
          onClick={(e) => {
            e.stopPropagation();
            onAnomaly(dep);
          }}
          sx={{ width: "100%", fontSize: "11px", padding: "4px 8px", minHeight: "30px", textTransform: "none" }}
        >
          {isAnomaly ? "Reported ⚠️" : "Anomaly"}
        </Button>
      </Box>
    </Box>
  );
});

DepartureRow.propTypes = {
  dep: PropTypes.object.isRequired,
  timeMode: PropTypes.string.isRequired,
  baseFeed: PropTypes.string.isRequired,
  onArrival: PropTypes.func.isRequired,
  onAnomaly: PropTypes.func.isRequired,
  reportStatus: PropTypes.string,
  onViewAnalysis: PropTypes.func
};

export default DepartureRow;